
import React, { useState, useEffect } from 'react';
import { getProximityColor } from '@/types/game';

interface ChartLinesProps {
  rounds: Array<{
    proximityScore?: number;
    userWord?: string;
    aiWord?: string;
  }>;
  maxRounds: number;
  innerRectX: number;
  innerRectY: number;
  innerRectWidth: number;
  innerPadding: number;
  rowHeight: number;
  fixedWidth: number;
}

export const ChartLines: React.FC<ChartLinesProps> = ({ 
  rounds, 
  maxRounds, 
  innerRectX, 
  innerRectY, 
  innerRectWidth, 
  innerPadding, 
  rowHeight,
  fixedWidth
}) => {
  // Track animated line widths
  const [animatedLines, setAnimatedLines] = useState<{[key: number]: number}>({});

  useEffect(() => {
    // When rounds change, reset and animate the lines
    const newAnimatedLines: {[key: number]: number} = {};
    
    // Initialize all lines to zero width
    rounds.forEach((round, index) => {
      if (round.proximityScore !== undefined) {
        newAnimatedLines[index] = 0;
      }
    });
    
    setAnimatedLines(newAnimatedLines);
    
    // Then animate them to full width after a small delay
    const timer = setTimeout(() => {
      const updatedLines: {[key: number]: number} = {};
      
      rounds.forEach((round, index) => {
        if (round.proximityScore !== undefined) {
          updatedLines[index] = 1; // 1 means full width according to score
        }
      });
      
      setAnimatedLines(updatedLines);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [rounds]);

  return (
    <>
      {Array.from({
        length: maxRounds
      }).map((_, index) => {
        const round = rounds[index];
        const score = round?.proximityScore;

        // Skip rendering if no score for this position
        if (score === undefined) return null;

        // Calculate the row's y position with inner padding
        const rowY = innerRectY + innerPadding + rowHeight * index + rowHeight / 2;

        // Calculate line width based on proximity score
        // 100 score (perfect match) = very short line (dot-like)
        // 0 score = full width line
        const maxLineWidth = innerRectWidth - (innerPadding * 2); // Adjust line width for inner padding
        const animationProgress = animatedLines[index] || 0;
        const fullLineWidth = maxLineWidth * (1 - score / 100);
        const lineWidth = fullLineWidth * animationProgress;
        
        return (
          <line 
            key={`line-${index}`} 
            x1={fixedWidth / 2 - lineWidth / 2} 
            y1={rowY} 
            x2={fixedWidth / 2 + lineWidth / 2} 
            y2={rowY} 
            stroke={getProximityColor(score)} 
            strokeWidth="6" 
            strokeLinecap="round" 
            className="transition-all duration-700 ease-out"
          />
        );
      })}
    </>
  );
};
